/**
 * Cart Photo Edit - JavaScript
 * 
 * Handles the edit functionality for photos in the cart page.
 * Opens a modal similar to photo-upload but in edit mode.
 * 
 * @version 2.0.0
 * @compatible Horizon Theme (Shopify Online Store 2.0)
 */

class CartPhotoEdit {
    constructor() {
        // State
        this.files = []; // Array of { dataUrl: string, id: string }
        this.editingItemKey = null;
        this.editingItemIndex = null;
        this.editingProductId = null;
        this.editingVariantId = null;
        this.manifestToken = null;
        this.uploadSessionId = null;
        this.workerUrl = '';
        this.manifestUpdateKey = null;
        this.maxFiles = 9;
        // Same limits as photo-upload.js: sources up to 50MB are accepted and
        // anything above 10MB is compressed in the browser before upload.
        this.maxFileSizeMB = 50;
        this.maxUploadBytes = 10 * 1024 * 1024;
        this.allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        this.isSubmitting = false;
        this.arW = 1;
        this.arH = 1;

        // Modal reference
        this.modal = null;
        this.fileInput = null;
        this.previewGrid = null;
        this.countDisplay = null;

        // Bind methods
        this.handleEditClick = this.handleEditClick.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.handleFileSelect = this.handleFileSelect.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.handleUpdateCart = this.handleUpdateCart.bind(this);
        this.handleRemoveFile = this.handleRemoveFile.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);

        // Initialize
        this.init();
    }

    init() {
        // Create modal structure
        this.createModal();

        // Bind edit buttons in cart
        this.bindEditButtons();

        // Listen for keyboard events
        document.addEventListener('keydown', this.handleKeyDown);
    }

    /**
     * Create the edit modal structure
     */
    createModal() {
        // Remove existing modal if any
        document.getElementById('cart-photo-edit-modal')?.remove();

        const modalHTML = `
        <div id="cart-photo-edit-modal" class="photo-upload-modal" role="dialog" aria-modal="true" aria-labelledby="cart-edit-title">
            <div class="photo-upload-modal__container">
                <header class="photo-upload-modal__header">
                    <h2 class="photo-upload-modal__title" id="cart-edit-title">Product preview</h2>
                    <button type="button" class="photo-upload-modal__close" aria-label="Close">&times;</button>
                </header>
                
                <div class="photo-upload-modal__body">
                    <div class="photo-upload-step photo-upload-step--preview" style="display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 0;">
                        <div class="photo-upload-preview" style="min-height: 0;">
                            <div class="photo-upload-preview__grid-wrapper">
                                <div class="photo-upload-preview__grid" data-testid="cart-edit-grid">
                                    <!-- Preview items will be inserted here -->
                                </div>
                                
                                <div class="photo-upload-preview__sidebar">
                                    <div class="photo-upload-preview__info">
                                        <h3>Product Preview</h3>
                                        <div class="photo-upload-preview__note">
                                            <span class="photo-upload-preview__note-icon"></span>
                                            <span>Area beyond the dotted lines will wrap around the edges of your magnets</span>
                                        </div>
                                        <p class="photo-upload-preview__tip">Click the "Crop" icon near a picture to resize or crop it</p>
                                        <p class="photo-upload-preview__count">
                                            <strong>Selected</strong> <span data-count>0</span> / <span data-max>9</span> photos
                                        </p>
                                        <p class="photo-upload-preview__count-hint" data-count-hint hidden style="color:#E981A4;font-size:14px;margin:8px 0 0;"></p>
                                    </div>
                                    
                                    <div class="cart-edit-actions">
                                        <button type="button" class="photo-upload-cancel-btn">Cancel</button>
                                        <button type="button" class="photo-upload-update-cart" data-testid="cart-edit-submit">
                                            SAVE
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>`;

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        // Cache references
        this.modal = document.getElementById('cart-photo-edit-modal');
        this.previewGrid = this.modal.querySelector('[data-testid="cart-edit-grid"]');
        this.countDisplay = this.modal.querySelector('[data-count]');
        this.maxDisplay = this.modal.querySelector('[data-max]');
        this.countHint = this.modal.querySelector('[data-count-hint]');

        // Bind modal events
        this.modal.querySelector('.photo-upload-modal__close')?.addEventListener('click', this.handleModalClose);
        this.modal.querySelector('.photo-upload-cancel-btn')?.addEventListener('click', this.handleModalClose);
        this.modal.querySelector('.photo-upload-update-cart')?.addEventListener('click', this.handleUpdateCart);

        // Close on backdrop click
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.handleModalClose();
            }
        });

        // Create hidden file input
        this.fileInput = document.createElement('input');
        this.fileInput.type = 'file';
        this.fileInput.accept = 'image/jpeg,image/png,image/webp,image/gif';
        this.fileInput.multiple = true;
        this.fileInput.style.display = 'none';
        this.fileInput.addEventListener('change', this.handleFileSelect);
        document.body.appendChild(this.fileInput);
    }

    /**
     * Bind edit buttons in cart
     */
    bindEditButtons() {
        document.querySelectorAll('[data-action="edit-photos"]').forEach(btn => {
            btn.addEventListener('click', this.handleEditClick);
        });

        // Also observe for dynamically added buttons
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        node.querySelectorAll?.('[data-action="edit-photos"]')?.forEach(btn => {
                            btn.removeEventListener('click', this.handleEditClick);
                            btn.addEventListener('click', this.handleEditClick);
                        });
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * Handle edit button click
     */
    handleEditClick(e) {
        const button = e.currentTarget;

        this.editingItemKey = button.dataset.itemKey;
        this.editingItemIndex = button.dataset.itemIndex;
        this.editingProductId = button.dataset.productId;
        this.editingVariantId = button.dataset.variantId;
        this.editingCartQuantity = parseInt(button.dataset.cartQuantity, 10) || 1;
        this.manifestToken = button.dataset.manifestToken || null;
        this.uploadSessionId = button.dataset.uploadSessionId || null;
        this.workerUrl = String(button.dataset.workerUrl || '').replace(/\/$/, '');
        this.manifestUpdateKey = null;

        // Read max files from variant data attribute (extracted from variant title in liquid)
        const maxFromData = parseInt(button.dataset.maxFiles, 10);
        if (maxFromData > 0) {
            this.maxFiles = maxFromData;
        }

        // Parse variant title for aspect ratio (e.g. "80×53mm" or "50x50 mm")
        this.arW = 1;
        this.arH = 1;
        const vTitle = (button.dataset.variantTitle || '').toLowerCase().replace(/×/g, 'x').replace(/\s/g, '');
        const sizeMatch = vTitle.match(/(\d+)x(\d+)mm/);
        if (sizeMatch) {
            const w = parseInt(sizeMatch[1], 10);
            const h = parseInt(sizeMatch[2], 10);
            if (w > 0 && h > 0) { this.arW = w; this.arH = h; }
        }

        // Decode and parse photos from data attribute
        const photosEncoded = button.dataset.photos || '';
        const photosString = decodeURIComponent(photosEncoded);
        let photoUrls = photosString.split('|||').filter(url => url && url.trim() !== '');

        if (photoUrls.length === 0 && button.dataset.galleryUrls) {
            try {
                const galleryUrls = JSON.parse(button.dataset.galleryUrls);
                if (Array.isArray(galleryUrls)) {
                    photoUrls = galleryUrls.filter(url => typeof url === 'string' && url.trim() !== '');
                }
            } catch (error) {
                console.warn('Unable to parse gallery URLs for photo edit:', error);
            }
        }

        // Convert URLs to file objects
        this.files = photoUrls.map((url, index) => ({
            dataUrl: url,
            id: 'existing_' + index + '_' + Math.random().toString(36).substr(2, 5),
            sourceIndex: index,
            originalFile: null,
            previewChanged: false
        }));

        // Never let the limit drop below photos already in the item
        if (this.files.length > this.maxFiles) {
            this.maxFiles = this.files.length;
        }

        // Update max files display
        if (this.maxDisplay) {
            this.maxDisplay.textContent = this.maxFiles;
        }

        // Open modal and render grid
        this.openModal();
    }

    /**
     * Saving requires exactly maxFiles photos: disable SAVE and show how many are missing.
     */
    updateSaveState() {
        const submitBtn = this.modal?.querySelector('.photo-upload-update-cart');
        const missing = this.maxFiles - this.files.length;
        if (submitBtn && !this.isSubmitting) {
            submitBtn.disabled = missing > 0;
        }
        if (this.countHint) {
            this.countHint.hidden = missing <= 0;
            this.countHint.textContent = missing > 0
                ? `Please add ${missing} more photo${missing === 1 ? '' : 's'} to save.`
                : '';
        }
    }

    /**
     * Open modal
     */
    openModal() {
        this.modal?.classList.add('active');
        document.body.style.overflow = 'hidden';
        this.renderPreviewGrid();
    }

    /**
     * Close modal
     */
    handleModalClose() {
        this.modal?.classList.remove('active');
        document.body.style.overflow = '';
        this.files = [];
        this.editingItemKey = null;
        this.editingCartQuantity = 1;
        this.isSubmitting = false;
    }

    /**
     * Handle ESC key
     */
    handleKeyDown(e) {
        if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
            this.handleModalClose();
        }
    }

    /**
     * Render preview grid
     */
    renderPreviewGrid() {
        if (!this.previewGrid) return;

        let html = '';

        // Render existing files
        this.files.forEach((item, index) => {
            html += `
            <div class="photo-upload-preview__item" data-id="${item.id}" style="aspect-ratio: ${this.arW} / ${this.arH};">
                <img src="${item.dataUrl}" alt="Photo ${index + 1}" />
                <div class="photo-upload-preview__actions">
                    <button type="button" class="photo-upload-preview__action-btn photo-upload-preview__action-btn--delete" data-action="delete" data-id="${item.id}" title="Delete">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                    </button>
                    <button type="button" class="photo-upload-preview__action-btn" data-action="crop" data-id="${item.id}" title="Crop">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/>
                            <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/>
                        </svg>
                    </button>
                    <button type="button" class="photo-upload-preview__action-btn" data-action="duplicate" data-id="${item.id}" title="Duplicate">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                    </button>
                </div>
            </div>`;
        });

        // Add "Add more" cell if under limit
        if (this.files.length < this.maxFiles) {
            html += `
            <div class="photo-upload-preview__add-more" data-testid="cart-edit-add-more" style="aspect-ratio: ${this.arW} / ${this.arH};">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                <span>Add more</span>
            </div>`;
        }

        this.previewGrid.innerHTML = html;

        // Apply grid layout based on current maxFiles
        this.updateGridLayout();

        // Update count display: "Selected X / Y"
        if (this.countDisplay) {
            this.countDisplay.textContent = this.files.length;
        }
        if (this.maxDisplay) {
            this.maxDisplay.textContent = this.maxFiles;
        }
        this.updateSaveState();

        // Bind delete buttons
        this.previewGrid.querySelectorAll('[data-action="delete"]').forEach(btn => {
            btn.addEventListener('click', this.handleRemoveFile);
        });

        // Bind crop buttons
        this.previewGrid.querySelectorAll('[data-action="crop"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const id = e.currentTarget.dataset.id;
                this.openCropModal(id);
            });
        });

        // Bind duplicate buttons
        this.previewGrid.querySelectorAll('[data-action="duplicate"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const id = e.currentTarget.dataset.id;
                this.handleDuplicateFile(id);
            });
        });

        // Bind add more
        this.previewGrid.querySelector('.photo-upload-preview__add-more')?.addEventListener('click', () => {
            this.fileInput?.click();
        });

        // Setup drag and drop on grid items
        this.setupDragDrop();
    }

    /**
     * Setup drag and drop on the preview grid
     */
    setupDragDrop() {
        const addMoreBtn = this.previewGrid.querySelector('.photo-upload-preview__add-more');
        if (!addMoreBtn) return;

        addMoreBtn.addEventListener('dragover', this.handleDragOver);
        addMoreBtn.addEventListener('dragleave', this.handleDragLeave);
        addMoreBtn.addEventListener('drop', this.handleDrop);
    }

    /**
     * Handle drag over
     */
    handleDragOver(e) {
        e.preventDefault();
        e.currentTarget.classList.add('drag-over');
    }

    /**
     * Handle drag leave
     */
    handleDragLeave(e) {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
    }

    /**
     * Handle drop
     */
    handleDrop(e) {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');

        const files = Array.from(e.dataTransfer?.files || []);
        this.processFiles(files);
    }

    /**
     * Handle file input change
     */
    handleFileSelect(e) {
        const files = Array.from(e.target?.files || []);
        this.processFiles(files);

        // Reset input to allow re-selecting same file
        if (this.fileInput) {
            this.fileInput.value = '';
        }
    }

    /** Mirrors compressPhotoToLimit in photo-upload.js (kept as methods so the two scripts never clash). */
    async decodePhoto(file) {
        if (window.createImageBitmap) {
            try {
                return await createImageBitmap(file, { imageOrientation: 'from-image' });
            } catch {
                // Fall back to <img> decoding below.
            }
        }
        const url = URL.createObjectURL(file);
        try {
            const image = new Image();
            image.src = url;
            await image.decode();
            return image;
        } finally {
            URL.revokeObjectURL(url);
        }
    }

    async compressPhotoToLimit(file, maxBytes) {
        const image = await this.decodePhoto(file);
        const canvas = document.createElement('canvas');
        try {
            const width = image.width;
            const height = image.height;
            // iOS Safari refuses canvases above ~16.7 megapixels.
            const fit = Math.min(1, Math.sqrt(16000000 / (width * height)));
            const context = canvas.getContext('2d');
            const attempts = [[1, 0.92], [1, 0.86], [0.85, 0.86], [0.72, 0.86], [0.6, 0.84], [0.5, 0.82]];
            for (const [scale, quality] of attempts) {
                canvas.width = Math.max(1, Math.round(width * fit * scale));
                canvas.height = Math.max(1, Math.round(height * fit * scale));
                context.fillStyle = '#fff';
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.drawImage(image, 0, 0, canvas.width, canvas.height);
                const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
                if (blob && blob.size <= maxBytes) {
                    const name = `${String(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;
                    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
                }
            }
            return null;
        } finally {
            image.close?.();
            canvas.width = 0;
            canvas.height = 0;
        }
    }

    /** Same rules and wording as describeUnsupportedPhoto in photo-upload.js. */
    describeUnsupportedPhoto(file) {
        const name = String(file?.name || '').toLowerCase();
        if (/\.(heic|heif)$/.test(name) || /image\/hei[cf]/.test(file?.type || '')) {
            return 'HEIC photos are not supported yet. Please save it as JPG or PNG (on iPhone: Settings › Camera › Formats › Most Compatible).';
        }
        if (!this.allowedTypes.includes(file?.type)) {
            return 'this file type is not supported. Please use a JPG, PNG, WebP or GIF photo.';
        }
        if (!file.size) return 'the file is empty.';
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > this.maxFileSizeMB) {
            return `the photo is ${sizeMB.toFixed(1)}MB, larger than the ${this.maxFileSizeMB}MB limit. Please choose a smaller copy.`;
        }
        return null;
    }

    /**
     * Process and validate selected files
     */
    async processFiles(files) {
        let validFiles = [];
        const rejected = [];
        let overLimit = 0;
        const startingCount = this.files.length;

        for (const file of files) {
            const problem = this.describeUnsupportedPhoto(file);
            if (problem) {
                console.warn(`Skipping ${file.name}: ${problem}`);
                rejected.push(`• ${file.name || 'Photo'}: ${problem}`);
                continue;
            }

            // Check max files limit
            if (this.files.length + validFiles.length >= this.maxFiles) {
                overLimit++;
                continue;
            }

            validFiles.push(file);
        }

        const notices = [];
        if (rejected.length) {
            notices.push(`${rejected.length === 1 ? 'This photo' : 'These photos'} could not be added:\n${rejected.join('\n')}`);
        }
        if (overLimit) {
            const plural = (count) => `${count} photo${count === 1 ? '' : 's'}`;
            notices.push(startingCount >= this.maxFiles
                ? `This item is already full (${this.maxFiles} of ${this.maxFiles} photos). Remove a photo to add another.`
                : `You selected ${plural(validFiles.length + overLimit)}, but this item holds ${this.maxFiles}. The first ${plural(validFiles.length)} ${validFiles.length === 1 ? 'was' : 'were'} added and ${overLimit} left out.`);
        }
        if (notices.length) alert(notices.join('\n\n'));

        // Compress large photos one at a time to keep memory low on phones.
        const failedCompressions = [];
        for (let index = 0; index < validFiles.length; index++) {
            const source = validFiles[index];
            if (source.size <= this.maxUploadBytes) continue;
            const compressed = await this.compressPhotoToLimit(source, this.maxUploadBytes).catch(() => null);
            if (compressed) {
                console.info(`[CartPhotoEdit] Compressed ${source.name} ${(source.size / 1048576).toFixed(1)}MB → ${(compressed.size / 1048576).toFixed(1)}MB`);
                validFiles[index] = compressed;
            } else {
                failedCompressions.push(`• ${source.name || 'Photo'}: this photo could not be reduced below 10MB. Please choose a smaller copy.`);
                validFiles[index] = null;
            }
        }
        validFiles = validFiles.filter(Boolean);
        if (failedCompressions.length) alert(`These photos could not be added:\n${failedCompressions.join('\n')}`);

        // Convert to data URLs
        for (const file of validFiles) {
            const dataUrl = await this.fileToDataUrl(file);

            this.files.push({
                dataUrl,
                id: 'new_' + Math.random().toString(36).substr(2, 9),
                sourceIndex: null,
                originalFile: file,
                previewChanged: true
            });
        }

        // Re-render grid and scroll to last item
        if (validFiles.length > 0) {
            this.manifestUpdateKey = null;
            this.renderPreviewGrid();
            this.scrollToLastItem();
        }
    }

    /**
     * Convert File to base64 data URL.
     * Preview quality: 800×800 at 85% JPEG for crisp grid display.
     * Cart submission: compressed separately via compressForCart (100×100/20%).
     */
    fileToDataUrl(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.onload = () => {
                    resolve(this.compressImage(img, 800, 0.85));
                };
                img.onerror = reject;
                img.src = reader.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    /**
     * Compress an image to target dimensions and quality.
     * Shared by fileToDataUrl and crop modal.
     */
    compressImage(img, maxSize, quality) {
        let width = img.width;
        let height = img.height;

        if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        return canvas.toDataURL('image/jpeg', quality);
    }

    /**
     * Re-compress a data URL for Shopify cart properties.
     * 800×800 @ 85% quality for crisp display in the product preview grid.
     * Uses same quality as initial upload preview to avoid degradation on edits.
     */
    compressForCart(dataUrl) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                resolve(this.compressImage(img, 800, 0.85));
            };
            img.onerror = () => resolve(dataUrl);
            img.src = dataUrl;
        });
    }

    /**
     * Remove a file
     */
    handleRemoveFile(e) {
        const id = e.currentTarget.dataset.id;
        this.files = this.files.filter(f => f.id !== id);
        this.manifestUpdateKey = null;
        this.renderPreviewGrid();
    }

    /**
     * Duplicate a file — clone and insert right after the original
     */
    handleDuplicateFile(fileId) {
        if (this.files.length >= this.maxFiles) {
            alert(`Maximum ${this.maxFiles} photos allowed. Remove a photo first.`);
            return;
        }

        const sourceIndex = this.files.findIndex(f => f.id === fileId);
        if (sourceIndex === -1) return;

        const source = this.files[sourceIndex];
        const clone = {
            dataUrl: source.dataUrl,
            id: 'dup_' + Math.random().toString(36).substr(2, 9),
            sourceIndex: source.sourceIndex,
            originalFile: source.originalFile,
            previewChanged: source.previewChanged
        };

        this.files.splice(sourceIndex + 1, 0, clone);
        this.manifestUpdateKey = null;
        this.renderPreviewGrid();
        this.scrollToLastItem();
    }

    /**
     * Update grid layout: always 3 columns, gap 10px.
     * Grid height is controlled by CSS flex, no JS maxHeight needed.
     */
    updateGridLayout() {
        if (!this.previewGrid) return;
        this.previewGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
        this.previewGrid.style.gap = '10px';
    }

    /**
     * Scroll the preview grid to show the last uploaded item
     */
    scrollToLastItem() {
        if (!this.previewGrid) return;
        requestAnimationFrame(() => {
            const items = this.previewGrid.querySelectorAll('.photo-upload-preview__item');
            if (items.length > 0) {
                const lastItem = items[items.length - 1];
                lastItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });
    }

    /**
     * Open crop modal for a specific image
     * @param {string} fileId - ID of the file to crop
     */
    openCropModal(fileId) {
        const file = this.files.find(f => f.id === fileId);
        if (!file) return;

        // Create crop modal
        const cropModal = document.createElement('div');
        cropModal.className = 'photo-upload-crop-modal';
        cropModal.innerHTML = `
            <div class="photo-upload-crop-modal__overlay"></div>
            <div class="photo-upload-crop-modal__container">
                <div class="photo-upload-crop-modal__header">
                    <h3>Crop Image</h3>
                    <button type="button" class="photo-upload-crop-modal__close">&times;</button>
                </div>
                <div class="photo-upload-crop-modal__body">
                    <div class="photo-upload-crop-modal__canvas-wrapper">
                        <canvas class="photo-upload-crop-modal__canvas"></canvas>
                        <div class="photo-upload-crop-modal__crop-area">
                            <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--nw" data-handle="nw"></div>
                            <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--ne" data-handle="ne"></div>
                            <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--sw" data-handle="sw"></div>
                            <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--se" data-handle="se"></div>
                        </div>
                    </div>
                    <div class="photo-upload-crop-modal__toolbar">
                        <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="flip-h" title="Flip Horizontal">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                                <path d="M16 3l4 4-4 4"/>
                                <path d="M20 7H4"/>
                                <path d="M8 21l-4-4 4-4"/>
                                <path d="M4 17h16"/>
                            </svg>
                            <span>Flip H</span>
                        </button>
                        <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="flip-v" title="Flip Vertical">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                                <path d="M3 8l4-4 4 4"/>
                                <path d="M7 4v16"/>
                                <path d="M21 16l-4 4-4-4"/>
                                <path d="M17 20V4"/>
                            </svg>
                            <span>Flip V</span>
                        </button>
                        <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="rotate-cw" title="Rotate 90°">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                                <path d="M23 4v6h-6"/>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                            </svg>
                            <span>Rotate</span>
                        </button>
                    </div>
                </div>
                <div class="photo-upload-crop-modal__footer">
                    <button type="button" class="photo-upload-crop-modal__cancel">Cancel</button>
                    <button type="button" class="photo-upload-crop-modal__apply">Apply Crop</button>
                </div>
            </div>
        `;

        document.body.appendChild(cropModal);

        // Transform state
        let flipH = false;
        let flipV = false;
        let rotation = 0; // 0, 90, 180, 270

        // Load image
        const img = new Image();
        if (String(file.dataUrl).startsWith('https://')) img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = cropModal.querySelector('.photo-upload-crop-modal__canvas');
            const ctx = canvas.getContext('2d');
            const wrapper = cropModal.querySelector('.photo-upload-crop-modal__canvas-wrapper');
            const cropArea = cropModal.querySelector('.photo-upload-crop-modal__crop-area');

            // Responsive max size: larger on desktop, smaller on mobile
            const isMobile = window.innerWidth <= 767;
            const maxSize = isMobile ? 400 : 600;

            /**
             * Get effective display dimensions based on rotation
             */
            const getDisplayDims = () => {
                const isRotated = rotation === 90 || rotation === 270;
                const srcW = isRotated ? img.height : img.width;
                const srcH = isRotated ? img.width : img.height;

                let dw = srcW;
                let dh = srcH;
                if (dw > maxSize || dh > maxSize) {
                    const ratio = Math.min(maxSize / dw, maxSize / dh);
                    dw = Math.round(srcW * ratio);
                    dh = Math.round(srcH * ratio);
                }
                return { displayWidth: dw, displayHeight: dh };
            };

            /**
             * Redraw the canvas with current transforms
             */
            const redrawCanvas = () => {
                const { displayWidth, displayHeight } = getDisplayDims();

                canvas.width = displayWidth;
                canvas.height = displayHeight;
                wrapper.style.width = displayWidth + 'px';
                wrapper.style.height = displayHeight + 'px';

                ctx.save();
                ctx.clearRect(0, 0, displayWidth, displayHeight);

                // Move to center for transforms
                ctx.translate(displayWidth / 2, displayHeight / 2);

                // Apply rotation
                ctx.rotate((rotation * Math.PI) / 180);

                // Apply flips
                ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

                // Draw image centered
                const isRotated = rotation === 90 || rotation === 270;
                const drawW = isRotated ? displayHeight : displayWidth;
                const drawH = isRotated ? displayWidth : displayHeight;
                ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

                ctx.restore();

                // Reset crop area to fit new dimensions with ratio
                const curDims = getDisplayDims();
                displayWidth = curDims.displayWidth;
                displayHeight = curDims.displayHeight;
                if (displayWidth / displayHeight > cropRatio) {
                    cropH = displayHeight;
                    cropW = Math.round(cropH * cropRatio);
                } else {
                    cropW = displayWidth;
                    cropH = Math.round(cropW / cropRatio);
                }
                cropX = Math.round((displayWidth - cropW) / 2);
                cropY = Math.round((displayHeight - cropH) / 2);
                updateCropAreaUI();
            };

            // Initial draw
            let { displayWidth, displayHeight } = getDisplayDims();
            canvas.width = displayWidth;
            canvas.height = displayHeight;
            wrapper.style.width = displayWidth + 'px';
            wrapper.style.height = displayHeight + 'px';
            ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

            // Setup crop area (ratio-constrained, centered)
            const minCropDim = 50;
            const cropRatio = this.arW / this.arH;
            let cropW, cropH;
            if (displayWidth / displayHeight > cropRatio) {
                cropH = displayHeight;
                cropW = Math.round(cropH * cropRatio);
            } else {
                cropW = displayWidth;
                cropH = Math.round(cropW / cropRatio);
            }
            let cropX = Math.round((displayWidth - cropW) / 2);
            let cropY = Math.round((displayHeight - cropH) / 2);

            const updateCropAreaUI = () => {
                cropArea.style.width = cropW + 'px';
                cropArea.style.height = cropH + 'px';
                cropArea.style.left = cropX + 'px';
                cropArea.style.top = cropY + 'px';
            };

            updateCropAreaUI();

            // --- Toolbar events ---
            cropModal.querySelector('[data-tool="flip-h"]').addEventListener('click', () => {
                flipH = !flipH;
                redrawCanvas();
            });
            cropModal.querySelector('[data-tool="flip-v"]').addEventListener('click', () => {
                flipV = !flipV;
                redrawCanvas();
            });
            cropModal.querySelector('[data-tool="rotate-cw"]').addEventListener('click', () => {
                rotation = (rotation + 90) % 360;
                redrawCanvas();
                // Update local display dims reference
                const dims = getDisplayDims();
                displayWidth = dims.displayWidth;
                displayHeight = dims.displayHeight;
            });

            // Drag and resize state
            let isDragging = false;
            let isResizing = false;
            let resizeHandle = null;
            let startX = 0, startY = 0, startCropX = 0, startCropY = 0, startCropW = 0, startCropH = 0;

            // Handle drag start on crop area (not on resize handles)
            const handleCropAreaDown = (e) => {
                const point = e.touches ? e.touches[0] : e;
                if (e.target.dataset.handle) return;

                isDragging = true;
                startX = point.clientX;
                startY = point.clientY;
                startCropX = cropX;
                startCropY = cropY;
                e.preventDefault();
            };

            cropArea.addEventListener('mousedown', handleCropAreaDown);
            cropArea.addEventListener('touchstart', handleCropAreaDown, { passive: false });

            // Handle resize start on handles
            cropModal.querySelectorAll('.photo-upload-crop-modal__resize-handle').forEach(handle => {
                const handleResizeDown = (e) => {
                    const point = e.touches ? e.touches[0] : e;
                    isResizing = true;
                    resizeHandle = (e.target || e.currentTarget).dataset.handle;
                    startX = point.clientX;
                    startY = point.clientY;
                    startCropX = cropX;
                    startCropY = cropY;
                    startCropW = cropW;
                    startCropH = cropH;
                    e.preventDefault();
                    e.stopPropagation();
                };
                handle.addEventListener('mousedown', handleResizeDown);
                handle.addEventListener('touchstart', handleResizeDown, { passive: false });
            });

            // Handle mouse/touch move for both drag and resize
            const handlePointerMove = (e) => {
                const point = e.touches ? e.touches[0] : e;
                const curDims = getDisplayDims();
                const dw = curDims.displayWidth;
                const dh = curDims.displayHeight;

                if (isDragging) {
                    const dx = point.clientX - startX;
                    const dy = point.clientY - startY;

                    cropX = Math.max(0, Math.min(dw - cropW, startCropX + dx));
                    cropY = Math.max(0, Math.min(dh - cropH, startCropY + dy));

                    updateCropAreaUI();
                } else if (isResizing) {
                    const dx = point.clientX - startX;
                    const dy = point.clientY - startY;
                    const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;

                    let newW, newH, newX, newY;

                    switch (resizeHandle) {
                        case 'se':
                            newW = Math.max(minCropDim, startCropW + delta);
                            newH = Math.round(newW / cropRatio);
                            newW = Math.min(newW, dw - startCropX);
                            newH = Math.min(newH, dh - startCropY);
                            if (newW / newH > cropRatio) { newW = Math.round(newH * cropRatio); }
                            else { newH = Math.round(newW / cropRatio); }
                            newX = startCropX;
                            newY = startCropY;
                            break;
                        case 'sw':
                            newW = Math.max(minCropDim, startCropW - dx);
                            newH = Math.round(newW / cropRatio);
                            newW = Math.min(newW, startCropX + startCropW);
                            newH = Math.min(newH, dh - startCropY);
                            if (newW / newH > cropRatio) { newW = Math.round(newH * cropRatio); }
                            else { newH = Math.round(newW / cropRatio); }
                            newX = startCropX + startCropW - newW;
                            newY = startCropY;
                            break;
                        case 'ne':
                            newW = Math.max(minCropDim, startCropW + dx);
                            newH = Math.round(newW / cropRatio);
                            newW = Math.min(newW, dw - startCropX);
                            newH = Math.min(newH, startCropY + startCropH);
                            if (newW / newH > cropRatio) { newW = Math.round(newH * cropRatio); }
                            else { newH = Math.round(newW / cropRatio); }
                            newX = startCropX;
                            newY = startCropY + startCropH - newH;
                            break;
                        case 'nw':
                            newW = Math.max(minCropDim, startCropW - delta);
                            newH = Math.round(newW / cropRatio);
                            newW = Math.min(newW, startCropX + startCropW);
                            newH = Math.min(newH, startCropY + startCropH);
                            if (newW / newH > cropRatio) { newW = Math.round(newH * cropRatio); }
                            else { newH = Math.round(newW / cropRatio); }
                            newX = startCropX + startCropW - newW;
                            newY = startCropY + startCropH - newH;
                            break;
                        default:
                            return;
                    }

                    // Validate bounds
                    if (newX >= 0 && newY >= 0 &&
                        newX + newW <= dw &&
                        newY + newH <= dh &&
                        newW >= minCropDim && newH >= minCropDim) {
                        cropW = newW;
                        cropH = newH;
                        cropX = newX;
                        cropY = newY;
                        updateCropAreaUI();
                    }
                }
            };

            const handlePointerUp = () => {
                isDragging = false;
                isResizing = false;
                resizeHandle = null;
            };

            document.addEventListener('mousemove', handlePointerMove);
            document.addEventListener('mouseup', handlePointerUp);
            document.addEventListener('touchmove', handlePointerMove, { passive: false });
            document.addEventListener('touchend', handlePointerUp);

            // Apply crop + transforms
            cropModal.querySelector('.photo-upload-crop-modal__apply').addEventListener('click', () => {
                const curDims = getDisplayDims();
                const dw = curDims.displayWidth;
                const dh = curDims.displayHeight;

                // Create a full-size transformed canvas first
                const isRotated = rotation === 90 || rotation === 270;
                const fullW = isRotated ? img.height : img.width;
                const fullH = isRotated ? img.width : img.height;

                const transformCanvas = document.createElement('canvas');
                transformCanvas.width = fullW;
                transformCanvas.height = fullH;
                const tCtx = transformCanvas.getContext('2d');

                tCtx.translate(fullW / 2, fullH / 2);
                tCtx.rotate((rotation * Math.PI) / 180);
                tCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

                const drawW = isRotated ? fullH : fullW;
                const drawH = isRotated ? fullW : fullH;
                tCtx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

                // Now crop from the transformed canvas
                const scaleX = fullW / dw;
                const scaleY = fullH / dh;

                const actualX = cropX * scaleX;
                const actualY = cropY * scaleY;
                const actualW = cropW * scaleX;
                const actualH = cropH * scaleY;

                const cropCanvas = document.createElement('canvas');
                cropCanvas.width = Math.round(actualW);
                cropCanvas.height = Math.round(actualH);
                const cropCtx = cropCanvas.getContext('2d');

                cropCtx.drawImage(
                    transformCanvas,
                    actualX, actualY, actualW, actualH,
                    0, 0, cropCanvas.width, cropCanvas.height
                );

                // Update file dataUrl at preview quality (800px/0.85)
                const tempImg = new Image();
                tempImg.onload = () => {
                    file.dataUrl = this.compressImage(tempImg, 800, 0.85);
                    file.previewChanged = true;
                    this.manifestUpdateKey = null;
                    cleanup();
                    this.renderPreviewGrid();
                };
                tempImg.src = cropCanvas.toDataURL('image/jpeg', 0.95);
            });

            // Cleanup on close
            const cleanup = () => {
                document.removeEventListener('mousemove', handlePointerMove);
                document.removeEventListener('mouseup', handlePointerUp);
                document.removeEventListener('touchmove', handlePointerMove);
                document.removeEventListener('touchend', handlePointerUp);
                cropModal.remove();
            };

            cropModal.querySelector('.photo-upload-crop-modal__close').addEventListener('click', cleanup);
            cropModal.querySelector('.photo-upload-crop-modal__cancel').addEventListener('click', cleanup);
            cropModal.querySelector('.photo-upload-crop-modal__overlay').addEventListener('click', cleanup);
        };

        img.src = file.dataUrl;
    }

    dataUrlToBlob(dataUrl) {
        const [header, encoded] = String(dataUrl).split(',');
        const type = header?.match(/:(.*?);/)?.[1] || 'image/jpeg';
        const binary = atob(encoded || '');
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
        return new Blob([bytes], { type });
    }

    async updateRemoteManifest() {
        if (!this.manifestToken || !this.workerUrl) return null;
        const form = new FormData();
        const layout = [];
        let previewUploadIndex = 0;
        let originalUploadIndex = 0;
        let requestBytes = 0;
        // Duplicated photos share one File; upload it once and point every
        // copy at the same uploaded original.
        const uploadIndexByFile = new Map();

        for (let index = 0; index < this.files.length; index++) {
            const item = this.files[index];
            const entry = {};
            if (Number.isInteger(item.sourceIndex)) {
                entry.sourceIndex = item.sourceIndex;
            } else if (item.originalFile) {
                if (!uploadIndexByFile.has(item.originalFile)) {
                    uploadIndexByFile.set(item.originalFile, originalUploadIndex++);
                    form.append('original', item.originalFile, item.originalFile.name || `photo-${index + 1}.jpg`);
                    requestBytes += item.originalFile.size;
                }
                entry.originalUploadIndex = uploadIndexByFile.get(item.originalFile);
            } else {
                throw new Error(`Original file ${index + 1} is unavailable. Please add it again.`);
            }

            if (item.previewChanged || !Number.isInteger(item.sourceIndex)) {
                entry.previewUploadIndex = previewUploadIndex++;
                const preview = this.dataUrlToBlob(item.dataUrl);
                requestBytes += preview.size;
                form.append('preview', preview, `preview-${index + 1}.jpg`);
            }
            layout.push(entry);
        }

        // The Worker accepts up to 50 MB per save; explain instead of failing.
        if (requestBytes > 48 * 1024 * 1024) {
            throw new Error(`The new photos add up to ${(requestBytes / 1048576).toFixed(0)}MB, more than can be saved at once. Please replace a few photos, save, then replace the rest.`);
        }

        if (!this.manifestUpdateKey) {
            const randomPart = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            this.manifestUpdateKey = `${this.manifestToken}:edit:${randomPart}`;
        }
        form.append('layout', JSON.stringify(layout));
        form.append('idempotencyKey', this.manifestUpdateKey);
        const response = await fetch(`${this.workerUrl}/manifest/${encodeURIComponent(this.manifestToken)}`, {
            method: 'POST', body: form,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !Array.isArray(payload.files) || payload.files.length !== this.files.length) {
            throw new Error(payload.error || 'Unable to update the original photo archive.');
        }
        this.files.forEach((item, index) => {
            item.dataUrl = payload.files[index].previewUrl || payload.files[index].url;
            item.sourceIndex = index;
            item.originalFile = null;
            item.previewChanged = false;
        });
        return payload;
    }

    /** Update the durable manifest first, then mutate the existing cart line. */
    async handleUpdateCart() {
        if (this.files.length < this.maxFiles) {
            const missing = this.maxFiles - this.files.length;
            alert(`Please add ${missing} more photo${missing === 1 ? '' : 's'}. This pack needs ${this.maxFiles} photos.`);
            return;
        }

        if (this.isSubmitting) return;
        this.isSubmitting = true;

        const submitBtn = this.modal.querySelector('.photo-upload-update-cart');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Updating...';
        submitBtn.disabled = true;

        try {
            submitBtn.textContent = this.manifestToken ? 'Updating originals...' : 'Updating...';
            const manifestResult = await this.updateRemoteManifest();

            const cartResponse = await fetch('/cart.js');
            if (!cartResponse.ok) throw new Error('Unable to read the cart.');
            const cart = await cartResponse.json();
            const existingItem = cart.items.find(item => item.key === this.editingItemKey);
            if (!existingItem) throw new Error('This cart item no longer exists. Please refresh the page.');

            const properties = {
                '_photo_count': this.files.length.toString(),
                '_upload_id': existingItem.properties?._upload_id || Date.now().toString()
            };

            // Preserve any non-photo, non-system properties from the original item
            if (existingItem && existingItem.properties) {
                for (const [key, value] of Object.entries(existingItem.properties)) {
                    // Skip photo properties, photo count, and upload_id
                    if (key.includes('Photo') || key.includes('Picture') ||
                        key === '_photo_count' || key === '_upload_id' ||
                        key === '_gallery_urls' || key === '_upload_method') {
                        continue;
                    }
                    properties[key] = value;
                }
            }

            if (this.manifestToken) properties._photo_manifest_token = this.manifestToken;
            if (this.uploadSessionId) properties._upload_session_id = this.uploadSessionId;

            for (let i = 0; i < this.files.length; i++) {
                const item = this.files[i];
                if (item.dataUrl && item.dataUrl.startsWith('https://')) {
                    properties[`_Photo ${i + 1}`] = item.dataUrl;
                } else {
                    properties[`_Photo ${i + 1}`] = await this.compressForCart(item.dataUrl);
                }
            }

            submitBtn.textContent = 'Saving cart...';
            const changeResult = await this.changeCartItem(this.editingItemKey, this.editingCartQuantity || 1, properties);
            if (changeResult.status && changeResult.status >= 400) throw new Error(changeResult.description || changeResult.message || 'Failed to update cart item');

            if (manifestResult && window.DearestPhotoOrderNote) {
                submitBtn.textContent = 'Preparing download link...';
                await window.DearestPhotoOrderNote.sync();
            }

            // Show success and close modal
            this.handleModalClose();

            // Refresh the cart page
            window.location.reload();

        } catch (error) {
            console.error('Error updating cart:', error);
            alert(error?.message || 'Failed to update cart. Please try again.');
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
            this.isSubmitting = false;
        }
    }

    /**
     * Update cart item quantity via Shopify AJAX API
     */
    updateCartItem(key, quantity) {
        return fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: key,
                quantity: quantity
            })
        }).then(res => res.json());
    }

    async changeCartItem(key, quantity, properties) {
        const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity, properties })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || `Cart update failed (${response.status}).`);
        return payload;
    }

    /**
     * Add item to cart via Shopify AJAX API
     * Uses JSON encoding with pre-compressed tiny images
     */
    addToCart(variantId, quantity, properties) {
        return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                id: parseInt(variantId),
                quantity: quantity,
                properties: properties
            })
        }).then(res => res.json());
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.cartPhotoEdit = new CartPhotoEdit();
    });
} else {
    window.cartPhotoEdit = new CartPhotoEdit();
}
